import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getGstVendorRows, inrFull } from '../data';
import { PageSizeSelect } from './PageSize';
import type {
	GstVendorFigure,
	GstVendorHeader,
	GstVendorRows,
	GstVendorRow,
	GstTraceMonth,
} from '../types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** String-slice, never `new Date()` — ledger and bank dates arrive as IST midnight stored as the
 *  previous day's 18:30 UTC, so parsing shifts the day in any browser west of UTC. */
const day = (d: unknown) => {
	if (typeof d !== 'string' || !d) return '—';
	const [y, m, dd] = d.slice(0, 10).split('-');
	if (!y || !m || !dd) return d;
	return `${dd} ${MONTHS[Number(m) - 1] ?? m} ${y.slice(-2)}`;
};

const num = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v));
const money = (v: unknown) => {
	const n = num(v);
	return n === null || Number.isNaN(n) ? '—' : inrFull(n);
};

interface Col {
	key: string;
	label: string;
	kind?: 'money' | 'date' | 'trace' | 'balance';
	/** long free text — gets the flexible column and a smaller face. */
	wide?: boolean;
}

/**
 * Columns per tab. The tab LIST is API-driven (header.tabs), but each tab's rows have their own
 * shape, so the interesting columns are named here rather than dumping every key. An unknown tab
 * still renders — see columnsFor() — so a fifth tab added server-side is not a blank screen.
 */
const TAB_COLUMNS: Record<string, Col[]> = {
	bank: [
		{ key: 'txn_date', label: 'Date', kind: 'date' },
		{ key: 'remarks', label: 'Narration', wide: true },
		{ key: 'bank', label: 'Bank' },
		{ key: 'matched_alias', label: 'Matched alias' },
		{ key: 'paid_out', label: 'Paid out', kind: 'money' },
		{ key: 'came_back', label: 'Came back', kind: 'money' },
	],
	zoho: [
		{ key: 'bill_date', label: 'Bill date', kind: 'date' },
		{ key: 'bill_number', label: 'Bill #' },
		{ key: 'status', label: 'Status' },
		{ key: 'total', label: 'Total', kind: 'money' },
		{ key: 'still_open', label: 'Still open', kind: 'money' },
	],
	ledger: [
		{ key: 'line_date', label: 'Date', kind: 'date' },
		{ key: 'particulars', label: 'Particulars', wide: true },
		{ key: 'vch_type', label: 'Voucher' },
		{ key: 'vch_no', label: 'No.' },
		{ key: 'they_billed_us', label: 'They billed us', kind: 'money' },
		{ key: 'we_paid_them', label: 'We paid them', kind: 'money' },
	],
	/* One row per UNBOOKED invoice — not per payment. The question this tab answers is "this
	   invoice is in the 2B and not in our books; did the money go out anyway?", so the invoice
	   identifies the row and the two trace columns are the answer. */
	trace: [
		{ key: 'invoice_number', label: 'Invoice #' },
		{ key: 'invoice_date', label: 'Date', kind: 'date' },
		{ key: 'invoice_value', label: 'Value', kind: 'money' },
		{ key: 'email_trace', label: 'Email trace', kind: 'trace' },
		{ key: 'bank_trace', label: 'Bank trace', kind: 'trace' },
		{ key: 'verdict', label: 'Verdict', wide: true },
	],
};

/** 'same month only' is the trap this tab exists to expose: money moved that month, but nothing
 *  ties it to this invoice. Colouring it like a hit would undo the point, so only an exact
 *  amount reads as green and 'same month only' is amber, not neutral. */
const TRACE_TONE = (v: string) =>
	v === 'exact amount'
		? 'bg-emerald-50 text-emerald-700 border-emerald-200'
		: v === 'same month only'
		? 'bg-amber-50 text-amber-700 border-amber-200'
		: 'bg-slate-100 text-slate-500 border-slate-200';

const monthLabel = (m: string) => {
	const [y, mm] = m.split('-');
	return `${MONTHS[Number(mm) - 1] ?? mm} ${y?.slice(-2)}`;
};

/**
 * The finding, not the detail. When no payment names an invoice, per-invoice tracing is
 * impossible and printing "not traced" 43 times reads as "we found nothing" — when what the data
 * actually shows is that the money left every single month.
 *
 * `covered` is arithmetic, never attribution: it says the month had enough money moving to have
 * paid these invoices, NOT that these payments paid them. A short month is the interesting one —
 * that is where money may genuinely never have gone — so shortfalls are the only red here.
 */
const TraceByMonth: React.FC<{ months: GstTraceMonth[]; totals: GstVendorRows['totals'] }> = ({
	months,
	totals,
}) => {
	const short = months.filter(m => !m.covered);
	return (
		<div className='mb-3'>
			<div className='mb-2 flex flex-wrap items-baseline gap-x-3 text-xs'>
				<span className='font-semibold text-gray-900'>Money that left, month by month</span>
				<span className='text-gray-500'>
					{short.length === 0
						? `every one of the ${months.length} months had more money leaving the bank than unbooked invoices`
						: `${short.length} of ${months.length} months are short — see the red rows`}
				</span>
			</div>
			<div className='overflow-x-auto rounded-lg border border-gray-200'>
				<table className='w-full text-xs'>
					<thead className='bg-gray-50 uppercase text-gray-500'>
						<tr>
							<th className='py-2 px-2 text-left'>Month</th>
							<th className='py-2 px-2 text-right'>Unbooked inv</th>
							<th className='py-2 px-2 text-right'>Unbooked value</th>
							<th className='py-2 px-2 text-right'>Bank txns</th>
							<th className='py-2 px-2 text-right'>Money that left</th>
							<th className='py-2 px-2 text-right'>Enough to cover?</th>
						</tr>
					</thead>
					<tbody className='divide-y divide-gray-100'>
						{months.map(m => (
							<tr key={m.month} className={m.covered ? '' : 'bg-rose-50'}>
								<td className='py-1.5 px-2 whitespace-nowrap'>{monthLabel(m.month)}</td>
								<td className='py-1.5 px-2 text-right font-mono'>{m.invoices}</td>
								<td className='py-1.5 px-2 text-right font-mono'>{money(m.unbooked_value)}</td>
								<td className='py-1.5 px-2 text-right font-mono text-gray-500'>{m.bank_debits}</td>
								<td className='py-1.5 px-2 text-right font-mono'>{money(m.bank_value)}</td>
								<td className='py-1.5 px-2 text-right'>
									{m.covered ? (
										<span className='text-emerald-700'>yes</span>
									) : (
										<span className='font-medium text-rose-700'>
											short {money(m.shortfall)}
										</span>
									)}
								</td>
							</tr>
						))}
					</tbody>
					<tfoot className='border-t-2 border-gray-200 bg-gray-50 font-medium'>
						<tr>
							<td className='py-2 px-2'>Total</td>
							<td className='py-2 px-2 text-right font-mono'>{totals.count}</td>
							<td className='py-2 px-2 text-right font-mono'>{money(totals.unbooked_value)}</td>
							<td className='py-2 px-2' />
							<td className='py-2 px-2 text-right font-mono'>
								{money(months.reduce((t, m) => t + m.bank_value, 0))}
							</td>
							<td className='py-2 px-2 text-right'>
								{months.length - short.length}/{months.length}
							</td>
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	);
};

/** Falls back to the row's own keys so an unrecognised tab degrades to a plain table. */
const columnsFor = (tab: string, rows: GstVendorRow[]): Col[] =>
	TAB_COLUMNS[tab] ??
	Object.keys(rows[0] ?? {})
		.filter(k => k !== 'id')
		.slice(0, 7)
		.map(k => ({ key: k, label: k.replace(/_/g, ' ') }));

/** `available: false` means we hold no data for this block — say so rather than printing ₹0,
 *  which reads as "we checked and it is zero". */
const Figure: React.FC<{ title: string; f: GstVendorFigure; lines: Array<[string, unknown]> }> = ({
	title,
	f,
	lines,
}) => (
	<div className={`rounded-lg border p-3 ${f.available ? 'border-gray-200' : 'border-gray-100 bg-gray-50'}`}>
		<div className='text-[11px] font-semibold uppercase tracking-wide text-gray-500'>{title}</div>
		{f.available ? (
			<>
				<div className='mt-1 font-mono text-lg font-semibold text-gray-900'>
					{money(lines[0]?.[1])}
				</div>
				{lines.slice(1).map(([label, v]) =>
					num(v) === null ? null : (
						<div key={label} className='text-[11px] text-gray-500'>
							{label} <span className='font-mono'>{money(v)}</span>
						</div>
					)
				)}
			</>
		) : (
			<div className='mt-1 text-sm text-gray-400'>No data</div>
		)}
		{/* Confidence is rendered verbatim: "amounts are hard; the vendor attribution is inferred
		    from narration text" is the difference between a fact and a guess. */}
		<div className='mt-1.5 text-[10px] leading-snug text-gray-400' title={f.source}>
			{f.confidence}
		</div>
	</div>
);

/**
 * The three headline figures: what the vendor says, what our books say, what the bank says.
 * Rendered on the main screen above the tab bar whenever a vendor with a GSTIN is selected.
 */
export const VendorFigures: React.FC<{ header: GstVendorHeader }> = ({ header }) => {
	const h = header.header;
	return (
		<div className='mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3'>
				<Figure
					title='Vendor recorded'
					f={h.vendor_recorded}
					lines={[
						['', h.vendor_recorded.billed],
						['received', h.vendor_recorded.received],
						['opening', h.vendor_recorded.opening_balance],
					]}
				/>
				<Figure
					title='Zoho says'
					f={h.zoho_says}
					lines={[
						['', h.zoho_says.billed],
						['still open', h.zoho_says.still_open],
					]}
				/>
				<Figure
					title='Bank says'
					f={h.bank_says}
					lines={[
						['', h.bank_says.paid],
						['came back', h.bank_says.came_back],
					]}
				/>
		</div>
	);
};

/**
 * One vendor tab's rows — bank, Zoho books, vendor ledger, or email/bank trace.
 *
 * GSTIN-scoped everywhere except the bank tab, which cannot be: bank statements carry no GSTIN,
 * so those rows are narration-matched through the vendor's alias list. The API says as much in
 * `note`, and that note is shown, not paraphrased.
 */
export const VendorTabTable: React.FC<{ gstin: string; tab: string; period: string }> = ({
	gstin,
	tab,
	period,
}) => {
	const [rows, setRows] = useState<GstVendorRows | null>(null);
	const [rowsErr, setRowsErr] = useState<string | null>(null);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState<number | 'all'>(50);
	/** '' = let the API pick (the most recent statement covering the window). */
	const [statement, setStatement] = useState('');
	/** Trace tab: the monthly table is the finding, the 44 invoice rows are the supporting
	 *  detail, so the detail starts folded away. */
	const [showTraceRows, setShowTraceRows] = useState(false);

	// Page 3 of a 50-a-page list does not exist once the size changes, so reset alongside the
	// filters rather than sending a page number the new size cannot honour.
	useEffect(() => {
		setPage(1);
	}, [tab, gstin, period, pageSize, statement]);

	// Statements belong to the ledger tab only, and one vendor's file names mean nothing to the
	// next, so the choice resets whenever either changes.
	useEffect(() => {
		setStatement('');
		setShowTraceRows(false);
	}, [tab, gstin]);

	useEffect(() => {
		if (!tab) return;
		let on = true;
		setRows(null);
		setRowsErr(null);
		getGstVendorRows(gstin, {
			tab,
			period: period || undefined,
			page,
			pageSize,
			statement: statement || undefined,
		})
			.then(r => on && setRows(r))
			.catch(e => on && setRowsErr(e?.message || 'Could not load these rows'));
		return () => {
			on = false;
		};
	}, [gstin, tab, period, page, pageSize, statement]);

	/* Running balance, the column a ledger is normally read down.
	   It is only meaningful when the visible rows start at the ledger's first line: a period
	   filter or page 2 drops everything before, so a cumulative sum would silently restart from
	   zero mid-stream and print a confident wrong closing figure. `line_seq === 1` is the direct
	   test for "we are looking at this from the beginning" — cheaper and truer than inferring it
	   from which filters happen to be set.
	   Built from raw debit/credit, not from they_billed_us/we_paid_them: those two exclude the
	   opening balance and credit notes respectively, so a balance built on them would not close. */
	const ledgerBalances = useMemo(() => {
		const d = rows?.data ?? [];
		if (tab !== 'ledger' || Number(d[0]?.line_seq) !== 1) return null;
		let bal = 0;
		return d.map(r => (bal += (num(r.debit) ?? 0) - (num(r.credit) ?? 0)));
	}, [rows, tab]);

	const isTrace = !!rows?.by_month;
	// On the trace tab the monthly table already answers the question; the row list only appears
	// when someone asks for it. Every other tab is its rows, so it always shows.
	const tableVisible = !isTrace || showTraceRows;

	const cols = useMemo(() => {
		const base = columnsFor(tab, rows?.data ?? []);
		return ledgerBalances ? [...base, { key: '__balance', label: 'Balance', kind: 'balance' as const }] : base;
	}, [tab, rows, ledgerBalances]);

	return (
		<div>
			{rows?.by_month && rows.by_month.length > 0 && (
				<TraceByMonth months={rows.by_month} totals={rows.totals} />
			)}

			{/* The ledger note explains that overlapping statements are deliberately NOT summed,
			    and the bank note that its rows are inferred. Both change how the numbers should be
			    read, so neither is collapsed into a tooltip. */}
			{rows?.note && (
				<p className='mb-2 rounded border border-amber-200 bg-amber-50/50 p-2 text-[11px] leading-snug text-amber-800'>
					{rows.note}
				</p>
			)}

			<div className='mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600'>
				{rows &&
					Object.entries(rows.totals).map(([k, v]) => (
						<span key={k}>
							{k.replace(/_/g, ' ')}{' '}
							<span className='font-mono font-medium text-gray-900'>
								{k === 'count' ? v.toLocaleString() : money(v)}
							</span>
						</span>
					))}
				{ledgerBalances && ledgerBalances.length > 0 && (
					<span>
						closing balance{' '}
						<span className='font-mono font-medium text-gray-900'>
							{money(ledgerBalances[ledgerBalances.length - 1])}
						</span>
					</span>
				)}
				<span className='ml-auto flex items-center gap-2'>
					{/* Vendor statements overlap — Nov-2025 covers Apr-24→Nov-25 and Mar-2026 covers
					    Apr-25→Feb-26 — so the API serves one and lists the rest rather than summing
					    them. This picker switches which one; it never adds them together. */}
					{(rows?.statements_available?.length ?? 0) > 1 && (
						<select
							value={statement}
							onChange={e => setStatement(e.target.value)}
							title='Which vendor statement to read. They overlap, so only one is shown at a time — summing them would double-count.'
							className='max-w-[16rem] truncate rounded border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500'>
							<option value=''>Most recent statement</option>
							{rows?.statements_available?.map(st => (
								<option key={st.source_file} value={st.source_file}>
									{st.source_file} ({st.lines_in_window} lines)
								</option>
							))}
						</select>
					)}
				</span>
			</div>

			{isTrace && (
				<button
					onClick={() => setShowTraceRows(v => !v)}
					className='mb-2 rounded border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50'>
					{showTraceRows
						? 'Hide the invoice-by-invoice detail'
						: `Show the ${rows?.pagination.total ?? ''} unbooked invoices`}
				</button>
			)}

			{tableVisible && (
			<div className='overflow-x-auto rounded-lg border border-gray-200'>
				<table className='w-full text-xs'>
					<thead className='bg-gray-50 uppercase text-gray-500'>
						<tr>
							{cols.map(c => (
								<th
									key={c.key}
									className={`whitespace-nowrap py-2 px-2 ${
										c.kind === 'money' ? 'text-right' : 'text-left'
									}`}>
									{c.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody className='divide-y divide-gray-100'>
						{rowsErr ? (
							<tr>
								<td colSpan={cols.length} className='p-4 text-center text-rose-600'>
									{rowsErr}
								</td>
							</tr>
						) : !rows ? (
							<tr>
								<td colSpan={cols.length} className='p-6 text-center text-gray-400'>
									<Loader2 className='mx-auto animate-spin' size={15} />
								</td>
							</tr>
						) : rows.data.length === 0 ? (
							<tr>
								<td colSpan={cols.length} className='p-6 text-center text-gray-400'>
									Nothing on this tab for the chosen period.
								</td>
							</tr>
						) : (
							rows.data.map((r, i) => (
								<tr key={(r.recon_id as number) ?? (r.id as number) ?? i} className='hover:bg-gray-50'>
									{cols.map(c => (
										<td
											key={c.key}
											className={`py-1.5 px-2 ${
												c.kind === 'money'
													? 'text-right font-mono whitespace-nowrap'
													: c.wide
													? 'min-w-[200px] max-w-xs truncate text-gray-600'
													: 'whitespace-nowrap'
											}`}
											title={c.wide ? String(r[c.key] ?? '') : undefined}>
											{c.kind === 'balance' ? (
												money(ledgerBalances?.[i])
											) : c.kind === 'money' ? (
												money(r[c.key])
											) : c.kind === 'date' ? (
												day(r[c.key])
											) : c.kind === 'trace' ? (
												<span
													className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-medium ${TRACE_TONE(
														String(r[c.key] ?? '')
													)}`}>
													{String(r[c.key] ?? 'none')}
												</span>
											) : (r[c.key] ?? '—') === '' ? (
												'—'
											) : (
												String(r[c.key] ?? '—')
											)}
										</td>
									))}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
			)}

			{tableVisible && rows && rows.pagination.total > 0 && (
				<div className='mt-2 flex items-center justify-between text-xs text-gray-500'>
					<span>
						{rows.pagination.total.toLocaleString()} rows
						{rows.pagination.all
							? ' · showing all'
							: ` · page ${rows.pagination.page} of ${rows.pagination.totalPages}`}
					</span>
					<div className='flex items-center gap-2'>
						{/* Lives next to Prev/Next, which is where someone looking to change the page
						    size actually looks. */}
						<span className='text-gray-400'>Show</span>
						<PageSizeSelect value={pageSize} onChange={setPageSize} />
						{!rows.pagination.all && (
							<>
								<button
									disabled={page <= 1}
									onClick={() => setPage(p => Math.max(1, p - 1))}
									className='rounded border border-gray-200 px-2 py-1 disabled:opacity-40'>
									Prev
								</button>
								<button
									disabled={page >= rows.pagination.totalPages}
									onClick={() => setPage(p => Math.min(rows.pagination.totalPages, p + 1))}
									className='rounded border border-gray-200 px-2 py-1 disabled:opacity-40'>
									Next
								</button>
							</>
						)}
					</div>
				</div>
			)}
		</div>
	);
};
